import type { ReactNode } from "react";

import { cx } from "@/lib/cx";
import { Button } from "@/ui/Button";
import { CloseIcon } from "@/ui/icons";

export type OverlayHeaderProps = {
  title: ReactNode;
  titleLabel?: string;
  meta?: ReactNode;
  leading?: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  closeIcon?: ReactNode;
  /** Lets Radix Dialog.Close own the shared close button without restyling it. */
  wrapClose?: (button: ReactNode) => ReactNode;
  /** Lets Radix Dialog.Title wrap the real heading through `asChild`. */
  wrapTitle?: (heading: ReactNode) => ReactNode;
  className?: string;
};

/** One 56px title/close grammar for every dialog, peek and sheet. */
export function OverlayHeader({
  title,
  titleLabel,
  meta,
  leading,
  actions,
  onClose,
  closeLabel = "Close",
  closeIcon = <CloseIcon />,
  wrapClose,
  wrapTitle,
  className,
}: OverlayHeaderProps) {
  const heading = (
    <h2 aria-label={titleLabel} className="busee-overlay-title flex min-w-0 truncate text-title text-ps1-gold">
      <span className="truncate">{title}</span>
    </h2>
  );
  const close = onClose ? (
    <Button variant="ghost" size="md" iconOnly aria-label={closeLabel} onClick={onClose} data-sfx="back">
      {closeIcon}
    </Button>
  ) : null;

  return (
    <header className={cx("flex min-h-14 shrink-0 items-center gap-3 hairline-b px-4", className)}>
      {leading ? <span className="flex shrink-0 items-center">{leading}</span> : null}
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        {wrapTitle ? wrapTitle(heading) : heading}
        {meta ? <span className="truncate text-label text-subtle">{meta}</span> : null}
      </div>
      {actions ? <span className="flex shrink-0 items-center gap-2">{actions}</span> : null}
      {close ? (wrapClose ? wrapClose(close) : close) : null}
    </header>
  );
}
